'use client';

import { NavigableButton } from '@carvana/ds-button';
import Card from '@carvana/ds-card';
import Carousel from '@carvana/ds-carousel';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import cx from 'classnames';
import { useContext } from 'react';
import { INTERACTION_TO_ACTION } from '~/constants/common/CarouselAnalytics';
import dealershipFamilyMap from '~/constants/dealership/dealershipFamilyMap';
import topPicksFamilyMap from '~/constants/dealership/topPicksFamilyMap';
import { useDealershipId } from '~/context/dealership/StaticContext';

function Header() {
  return (
    <div className="flex flex-col gap-y-lg md:gap-y-xl">
      <h2 className="text-text-strong t-heading-xl md:t-heading-2xl">
        Top picks
      </h2>
      <p className="text-text-default t-body-sm md:t-body-md">
        Discover the models our customers keep coming back for—powerful,
        versatile and built to impress.
      </p>
    </div>
  );
}

export default function TopPicks() {
  const { trackEvent } = useContext(AnalyticsContext);
  const dealershipId = useDealershipId();

  const dealershipFamily = dealershipId
    ? dealershipFamilyMap[dealershipId]
    : null;

  return dealershipFamily && topPicksFamilyMap[dealershipFamily] ? (
    <div className="bg-canvas-default py-6xl md:py-7xl">
      <Carousel
        ariaLabel="Top picks"
        classes={{
          slide: cx(
            'pl-2xl md:pl-3xl lg:pl-4xl',
            'last:pr-2xl md:last:pr-3xl lg:last:pr-4xl'
          ),
          slides: 'gap-0',
          topSection: 'px-2xl md:px-3xl lg:px-4xl',
          wrapper: 'mx-auto max-w-[calc(1272px+var(--spacing-4xl)*2)]'
        }}
        headerContent={<Header />}
        hideScrollBar
        hidePagination
        trackEvent={event => {
          trackEvent('Top picks', {
            ...event,
            action: INTERACTION_TO_ACTION[event.interactionType]
          });
        }}
        slideByVisible
      >
        {topPicksFamilyMap[dealershipFamily].map(vehicle => (
          <Card
            key={vehicle.name}
            media={<img src={vehicle.image} alt={vehicle.name} />}
            className="h-full"
            classes={{ content: 'flex flex-col gap-y-2xl', media: 'flex' }}
          >
            <div className="flex flex-col gap-y-lg">
              <h3 className="text-text-strong t-heading-lg">{vehicle.name}</h3>
              <p className="text-text-default t-body-sm">{vehicle.blurb}</p>
            </div>
            <NavigableButton
              variant="secondary"
              size="md"
              width="fit-content"
              href={`/cars/filters?cvnaid=${vehicle.cvnaId}`}
              data-adobe-id="view-inventory"
              data-analytics-id="Top picks"
              data-analytics-attributes={JSON.stringify({
                item: 'View inventory'
              })}
              data-testid="view-inventory"
            >
              View inventory
            </NavigableButton>
          </Card>
        ))}
      </Carousel>
    </div>
  ) : null;
}
